import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getSuggestions } from '../../data/suggestions';

const InlineEditorWithSuggestions = ({ 
  value, 
  onSave, 
  onCancel, 
  className = "", 
  multiline = false, 
  placeholder = "Click to edit",
  fieldType = "general", // "skill", "school", "certificate", "general"
  showSuggestions = true
}) => {
  const [editValue, setEditValue] = useState(value || '');
  const [isEditing, setIsEditing] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [showSuggestionsDropdown, setShowSuggestionsDropdown] = useState(false);
  const [isSuggestionSelected, setIsSuggestionSelected] = useState(false);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const suggestionTimeoutRef = useRef(null);
  const useId = React.useId || (() => 'suggestions-listbox');
  const listboxId = useId();

  // Update edit value when value prop changes
  useEffect(() => {
    if (!isEditing) {
      setEditValue(value || '');
    }
  }, [value, isEditing]);



  // Focus and select text when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      // Initialize editValue when editing starts
      setEditValue(value || '');
      inputRef.current.focus();
      if (!multiline) {
        inputRef.current.select();
      }
      // Trigger suggestions after a small delay to ensure editValue is set
      if (showSuggestions && fieldType !== "general") {
        suggestionTimeoutRef.current = setTimeout(() => {
          const newSuggestions = getSuggestions(value || '', fieldType);
          setSuggestions(newSuggestions);
          setShowSuggestionsDropdown(newSuggestions.length > 0);
        }, 100);
      }
    }
    return () => {
      if (suggestionTimeoutRef.current) {
        clearTimeout(suggestionTimeoutRef.current);
      }
    };
  }, [isEditing, multiline, showSuggestions, fieldType, value]);



  // Generate suggestions based on input
  useEffect(() => {
    if (isEditing && showSuggestions && fieldType !== "general" && !isSuggestionSelected) {
      const newSuggestions = getSuggestions(editValue, fieldType);
      setSuggestions(newSuggestions);
      setShowSuggestionsDropdown(newSuggestions.length > 0);
      setSelectedSuggestionIndex(-1);
    } else if (!isEditing) {
      setSuggestions([]);
      setShowSuggestionsDropdown(false);
      setSelectedSuggestionIndex(-1);
      setIsSuggestionSelected(false);
    }
  }, [editValue, isEditing, showSuggestions, fieldType, isSuggestionSelected]);

  const handleSave = useCallback(() => {
    onSave(editValue);
    setIsEditing(false);
    setSuggestions([]);
    setShowSuggestionsDropdown(false);
    setIsSuggestionSelected(false);
  }, [editValue, onSave]);

  const handleCancel = useCallback(() => {
    setEditValue(value || '');
    setIsEditing(false);
    setSuggestions([]);
    setShowSuggestionsDropdown(false);
    setIsSuggestionSelected(false);
    if (onCancel) onCancel();
  }, [value, onCancel]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      if (selectedSuggestionIndex >= 0 && suggestions[selectedSuggestionIndex]) {
        const selectedSuggestion = suggestions[selectedSuggestionIndex];
        setEditValue(selectedSuggestion);
        setSuggestions([]);
        setShowSuggestionsDropdown(false);
        setSelectedSuggestionIndex(-1);
      } else {
        handleSave();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (showSuggestionsDropdown && suggestions.length > 0) {
        setSelectedSuggestionIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (showSuggestionsDropdown && suggestions.length > 0) {
        setSelectedSuggestionIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
      }
    } else if (e.key === 'Tab') {
      e.preventDefault();
      if (selectedSuggestionIndex >= 0 && suggestions[selectedSuggestionIndex]) {
        const selectedSuggestion = suggestions[selectedSuggestionIndex];
        setEditValue(selectedSuggestion);
        setSelectedSuggestionIndex(-1);
      }
    }
  }, [handleSave, handleCancel, multiline, selectedSuggestionIndex, suggestions, showSuggestionsDropdown]);

  const handleSuggestionClick = useCallback((suggestion) => {
    setEditValue(suggestion);
    setIsSuggestionSelected(true);
    setSuggestions([]);
    setShowSuggestionsDropdown(false);
    setSelectedSuggestionIndex(-1);
    
    // Update the input field value directly and focus it
    if (inputRef.current) {
      inputRef.current.value = suggestion;
      inputRef.current.focus();
    }
  }, []);



  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setEditValue(newValue);
    setIsSuggestionSelected(false); // Reset flag when user types
    setSelectedSuggestionIndex(-1);
  };

  if (isEditing) {
    const Component = multiline ? 'textarea' : 'input';
    return (
      <div className="relative">
        <Component
          ref={inputRef}
          type={multiline ? undefined : "text"}
          className={"border border-gray-300 rounded focus:border-primary-500 focus:ring-0 " + className}
          value={editValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          role={multiline ? undefined : "combobox"}
          aria-expanded={showSuggestionsDropdown && suggestions.length > 0}
          aria-autocomplete="list"
          aria-controls={showSuggestionsDropdown ? listboxId : undefined}
          aria-activedescendant={selectedSuggestionIndex >= 0 ? `${listboxId}-option-${selectedSuggestionIndex}` : undefined}
          onBlur={(e) => {
            // Check if the blur is due to clicking a suggestion
            const relatedTarget = e.relatedTarget;
            if (suggestionsRef.current && suggestionsRef.current.contains(relatedTarget)) {
              // Don't save if clicking on a suggestion
              return;
            }
            
            // Save immediately on blur
            handleSave();
          }}
          rows={multiline ? 8 : undefined}
          spellCheck="true"
          style={multiline ? { minWidth: '100%', minHeight: '200px' } : undefined}
        />
        
        {/* Suggestions Dropdown */}
        {showSuggestionsDropdown && suggestions.length > 0 && (
          <div
            ref={suggestionsRef}
            id={listboxId}
            role="listbox"
            aria-label="Suggestions"
            className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto"
          >
            <div className="px-3 py-1 text-xs text-gray-500 border-b border-gray-200 bg-gray-50" aria-hidden="true">
              Suggestions ({suggestions.length})
            </div>
            {suggestions.map((suggestion, index) => (
              <div
                key={index}
                id={`${listboxId}-option-${index}`}
                role="option"
                aria-selected={index === selectedSuggestionIndex}
                tabIndex={-1}
                className={`px-3 py-2 cursor-pointer text-sm border-b border-gray-100 last:border-b-0 ${
                  index === selectedSuggestionIndex
                    ? 'bg-primary-100 text-primary-900'
                    : 'hover:bg-gray-100 text-gray-900'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSuggestionClick(suggestion);
                }}
                onMouseEnter={() => setSelectedSuggestionIndex(index)}
              >
                {suggestion}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      className={"cursor-text hover:bg-gray-50 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 rounded " + className}
      onClick={() => setIsEditing(true)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsEditing(true); } }}
      aria-label={value ? `Edit: ${value}` : placeholder}
    >
      {value || <span className="text-gray-400">{placeholder}</span>}
    </div>
  );
};

export default InlineEditorWithSuggestions;
